<script setup>
import { useAuthStore } from '@/stores/authStore.js'
import AuthedLayout from '@/layouts/AuthedLayout.vue'
import { Format } from '@/models/utils/FormatUtils.js'
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { computed, onMounted, ref } from 'vue'
import { documentsApi } from '@/api/Documents/DocumentsApi.js'
import { Banner, Toaster } from '@/models/utils/Toaster.js'
import { Tenants } from '@/models/tenants/Tenants.js'
import CheckBox from '@/components/Forms/CheckBox.vue'
import PortalDataTable from '@/components/DataTable/PortalDataTable.vue'
import { DataTableEndpointDefinition } from '@/models/datatable/DataTableEndpointDefinition.js'

const authStore = useAuthStore();
const toaster = new Toaster()
const banner = new Banner()
const includeArchived = ref(false)
const archiveAfterDays = ref(0)

const table = ref(null)
const columns = [
	{ field: "name", title: "Name", sort: true, search: true },
	{ field: "tags", title: "Tags", sort: false, width: '40%' },
	{ field: "updated_at", title: "Date", sort: true, width: '1%' },
]

const advancedSearchParams = computed(() => {
	return [
		{
			"id": 'archive',
			"value": includeArchived.value
		}
	]
})

const endpoints = [
	new DataTableEndpointDefinition({
		endpointRoute: 'documents/published',
		endpointParams: { query_config: { "organization_id": authStore.activeOrganizationId, "include_access_logs": false, "include_status_changes": false, "include_tags": true } },
		advancedSearchParams: advancedSearchParams,
		initialSortColumn: 'updated_at',
		initialSortDirection: 'desc',
		searchPlaceholder: 'Search by name',
		showCheckboxes: true,
		columns: columns,
	})
]

const isDownloading = ref(false)
const selectedDocuments = ref([])

onMounted(() => {
	archiveAfterDays.value = Number(JSON.parse(authStore.getAuth.tenantInfo().settings_json)?.documents_archive_after_days?.value)
})

function rowSelected(rows) {
	selectedDocuments.value = rows
}

function toggleArchivedContent() {
	table.value.loadData()
}

function onDownloadClicked()
{
	isDownloading.value = true

	toaster.info('Requesting download...', { timeout: false })

	documentsApi.download(
		'publish',
		selectedDocuments.value,
		authStore.getAuth.tenantInfo().name,
		authStore.getActiveOrganization.organization_name,
		() => {
			isDownloading.value = false

			toaster.clear()
			toaster.success((selectedDocuments.value.length > 1 ? 'Documents' : 'Document') + ' downloaded successfully.')
		},
		(p_err) => {
			isDownloading.value = false
			handleDownloadError(p_err, selectedDocuments.value)
		}
	)
}

async function handleDownloadError(p_err, filteredDocuments)
{
	try
	{
		const devEnvEh = Tenants.isDevHost() && authStore.isPortalAdmin;
		const text = await p_err.response.data.text();
		const data = JSON.parse(text);

		toaster.clear()

		if (data['errors'] && data['errors'].length > 0)
		{
			const err_msgs = data['errors'].map(p_item => p_item.detail);
			if (devEnvEh)
			{
				banner.error("Dev Only Banner:<br>" + err_msgs.join('<br/>'));
			}

			const err = data['errors'].find((p_item) => p_item.status === 403);
			if (err)
			{
				toaster.error('You do not have permission to download documents.');
			}
			else
			{
				toaster.error((filteredDocuments.length > 1 ? 'Documents' : 'Document') + ' failed to download.')
			}
		}
		else
		{
			toaster.error((filteredDocuments.length > 1 ? 'Documents' : 'Document') + ' failed to download.')
		}
	}
	catch(e)
	{
		console.error(e)
	}
}

</script>

<template>
	<AuthedLayout>
		<p class="dashboard-title">Documents</p>

		<div class="p-8 rounded-xl bg-neutral border border-neutral-subtle card-shadow">
			<div class="flex flex-row mb-4">
				<p class="admin-center-subtitle">Published</p>
				<SubmitButton v-if="authStore.isPortalAdmin || authStore.canDownloadDocuments" label="Download" @click="onDownloadClicked" size="small" :disabled="selectedDocuments.length === 0 || isDownloading" :loading="isDownloading"/>
			</div>

			<PortalDataTable
				ref="table"
				:endpoints="endpoints"
				@row-selected="rowSelected"
			>
				<template #advanced-search>
					<div class="flex flex-row mr-1 mt-1 justify-end">
						<div class="flex w-full justify-end overflow-hidden">
							<Popper :content="`Documents are considered to be archived after ${archiveAfterDays} days.`" hover arrow placement="bottom-start">
								<CheckBox input-id="cbx_archive" label="Include archived content" label-location="left" class="my-2" :checked="false" v-model="includeArchived" @checked-changed="toggleArchivedContent" />
							</Popper>
						</div>
					</div>
				</template>

				<template #name="data">
					<div class="userpilot-mask">
						{{ data.value.name }}
					</div>
				</template>

				<template #tags="data">
					<div class="flex flex-wrap pt-2">
						<span v-for="(tag) in data.value.tags" :key="tag.id" class="inline text-accent-on-color bg-accent py-1 px-2 rounded mb-2 mr-2">
							{{ tag.name}}
						</span>
					</div>
				</template>

				<template #updated_at="data">
					<div class="whitespace-nowrap">
						{{ Format.date(data.value.updated_at) }}
					</div>
				</template>
			</PortalDataTable>
		</div>
	</AuthedLayout>
</template>

<style scoped>
/* Hide the header row checkbox since it doesn't fire the "rowSelect" event */
:deep(.bh-table-responsive table thead tr th .bh-checkbox) {
	@apply hidden;
}
</style>